export interface MotionAttachment {
  episodeId: string;
  backgroundSha256: string;
  characterPackageHashes: Record<string, string>;
  videoSha256: string;
  url: string;
  receiptSha256: string;
  qualificationIdentity: string;
  sourceFrameSha256: string;
  posterUrl: string;
}

const hash = (value: unknown): value is string => typeof value === "string" && /^[a-f0-9]{64}$/.test(value);
export function acceptedMotion(value: unknown, episodeId: string, backgroundHash: string | undefined,
  characterHashes: Record<string, string>): MotionAttachment | null {
  if (!value || typeof value !== "object" || !hash(backgroundHash)) return null;
  const item = value as Partial<MotionAttachment>;
  if (item.episodeId !== episodeId || item.backgroundSha256 !== backgroundHash || !hash(item.videoSha256) ||
      !hash(item.receiptSha256) || !hash(item.sourceFrameSha256) || typeof item.posterUrl !== "string" ||
      typeof item.url !== "string" || !item.characterPackageHashes ||
      !/^\/api\/background-motion\/[a-f0-9]{64}\/[a-f0-9]{64}$/.test(item.url) || !item.url.endsWith(`/${item.videoSha256}`)) return null;
  const identity = item.url.split("/")[3];
  if (item.posterUrl !== `/api/background-motion-poster/${identity}/${item.sourceFrameSha256}`) return null;
  const keys = Object.keys(characterHashes);
  if (keys.length === 0 || Object.keys(item.characterPackageHashes).length !== keys.length ||
      keys.some(key => !hash(characterHashes[key]) || item.characterPackageHashes?.[key] !== characterHashes[key])) return null;
  return item as MotionAttachment;
}
export function acceptedMotionUrl(value: unknown, episodeId: string, backgroundHash: string | undefined,
  characterHashes: Record<string, string>): string | null {
  return acceptedMotion(value, episodeId, backgroundHash, characterHashes)?.url ?? null;
}

/** Dialogue state is intentionally absent: environmental motion continues while the cast is idle. */
export function motionMayPlay(hidden: boolean, visible: boolean, reducedMotion: boolean, saveData: boolean,
  failed: boolean): boolean {
  return !hidden && visible && !reducedMotion && !saveData && !failed;
}

export interface MotionVideo {
  play(): Promise<void>;
  pause(): void;
}

/** A cached image can finish before React's passive effects run. */
export function motionPosterLoaded(image: { complete: boolean; naturalWidth: number; currentSrc: string } | null,
  expectedUrl: string | null): boolean {
  return Boolean(expectedUrl && image?.complete && image.naturalWidth > 0 && image.currentSrc === expectedUrl);
}
export async function synchronizeMotion(video: MotionVideo, enabled: boolean,
  isCurrent: () => boolean = () => true): Promise<boolean> {
  if (!enabled) { if (isCurrent()) video.pause(); return false; }
  try { await video.play(); return isCurrent(); }
  catch { if (isCurrent()) video.pause(); return false; }
}
